import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import { hvacProducts, accessories } from '../data/hvacProducts';
import './Home.css';

const Home: React.FC = () => {
  const fadeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    fadeRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const addFadeRef = (el: HTMLDivElement | null) => {
    if (el && !fadeRefs.current.includes(el)) {
      fadeRefs.current.push(el);
    }
  };

  const multiMatchConfigs = [
    { capacity: '18K BTU', zones: '2 zones', models: 'Wall Mount, Slim Duct' },
    { capacity: '27K BTU', zones: '3 zones', models: 'Wall Mount, Ceiling/Floor, Slim Duct' },
    { capacity: '36K BTU', zones: '4 zones', models: 'Wall Mount, Ceiling/Floor, Slim Duct, Cassette' },
    { capacity: '42K BTU', zones: '5 zones', models: 'Wall Mount, Ceiling/Floor, Slim Duct, Cassette' },
  ];

  return (
    <div className="home">
      <Hero
        title="Premium Heat Pump Systems for New England"
        subtitle="Authorized TCL HVAC distributor serving contractors and builders with high-efficiency heat pumps, expert support, and fast local fulfillment."
        primaryCta={{ text: 'Explore Products', to: '/products' }}
        secondaryCta={{ text: 'Contact Us', to: '/contact' }}
        background="gradient-teal"
      />

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="container stats-bar__grid">
          <div className="stats-bar__item">
            <span className="stats-bar__value">6</span>
            <span className="stats-bar__label">TCL Series</span>
          </div>
          <div className="stats-bar__item">
            <span className="stats-bar__value">25.0</span>
            <span className="stats-bar__label">Max SEER2</span>
          </div>
          <div className="stats-bar__item">
            <span className="stats-bar__value">60K</span>
            <span className="stats-bar__label">Max BTU/h</span>
          </div>
          <div className="stats-bar__item">
            <span className="stats-bar__value">10yr</span>
            <span className="stats-bar__label">Max Warranty</span>
          </div>
        </div>
      </div>

      {/* Product Showcase */}
      {hvacProducts.map((product, index) => (
        <section
          key={product.id}
          className={`section ${index % 2 === 0 ? 'section--dark' : 'section--charcoal'}`}
        >
          <div className="container fade-in" ref={addFadeRef}>
            <div className={`product__layout ${index % 2 !== 0 ? 'product__layout--reverse' : ''}`}>
              <div className="product__visual">
                <img
                  src={product.image}
                  alt={product.name}
                  className="product__image"
                />
              </div>
              <div className="product__info">
                <span className="product__label">{product.category}</span>
                <h2 className="product__title">{product.name}</h2>
                <p className="product__subtitle">{product.tagline}</p>
                {product.capacityRange && (
                  <div className="capacity-badges">
                    {product.capacityRange.split(' – ').length > 1 ? (
                      product.capacityRange.split(' – ').map((cap) => (
                        <span key={cap} className="capacity-badge">{cap}</span>
                      ))
                    ) : (
                      <span className="capacity-badge active">{product.capacityRange}</span>
                    )}
                  </div>
                )}
                <div className="specs__grid">
                  {product.highlights.map((h) => (
                    <div key={h.label} className="spec">
                      <span className="spec__value">{h.value}</span>
                      <span className="spec__label">{h.label}</span>
                    </div>
                  ))}
                </div>
                <div className="home__product-actions">
                  <Link to={`/products/${product.id}`} className="btn btn--primary btn--sm">
                    View Details
                  </Link>
                  <Link to="/contact" className="btn btn--outline-white btn--sm">
                    Request Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Multi-Match Configuration Guide */}
      <section className="section section--dark">
        <div className="container fade-in" ref={addFadeRef}>
          <div className="text-center">
            <span className="section__label">Configuration Guide</span>
            <h2 className="section__title">Multi-Match System Combinations</h2>
            <p className="section__subtitle">
              Choose the right outdoor unit capacity and pair it with your preferred indoor unit types.
            </p>
          </div>
          <div className="accordion">
            {multiMatchConfigs.map((config, i) => (
              <div key={i} className="accordion__item">
                <button
                  className={`accordion__trigger ${openAccordion === i ? 'active' : ''}`}
                  onClick={() => setOpenAccordion(openAccordion === i ? null : i)}
                >
                  <span>{config.capacity} Outdoor Unit — {config.zones}</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <div className={`accordion__content ${openAccordion === i ? 'open' : ''}`}>
                  <div className="accordion__body">
                    <p style={{ color: 'var(--color-gray-500)', fontSize: 'var(--font-size-small)' }}>
                      Compatible indoor units: {config.models}
                    </p>
                    <p style={{ color: 'var(--color-gray-500)', fontSize: 'var(--font-size-small)', marginTop: '0.5rem' }}>
                      Each zone can be independently controlled with wall mount (9K-24K), ceiling & floor (9K-18K), slim duct (9K-24K), or cassette (9K-18K) units.
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accessories */}
      <section className="section section--charcoal">
        <div className="container fade-in" ref={addFadeRef}>
          <div className="text-center">
            <span className="section__label">Installation Accessories</span>
            <h2 className="section__title">Everything You Need for a Clean Install</h2>
          </div>
          <div className="card-grid" style={{ marginTop: 'var(--space-2xl)' }}>
            {accessories.map((acc) => (
              <div key={acc.id} className="card">
                <h3 className="card__title">{acc.name}</h3>
                <p className="card__text">{acc.description}</p>
                <div className="home__acc-specs">
                  {acc.specs.map((s) => (
                    <div key={s.label} className="home__acc-spec-row">
                      <span>{s.label}</span>
                      <span>{s.value}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact" className="btn btn--outline-teal btn--sm" style={{ marginTop: 'var(--space-md)' }}>
                  Request Pricing
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Lexington Air */}
      <section className="section section--dark">
        <div className="container fade-in" ref={addFadeRef}>
          <div className="text-center">
            <span className="section__label">Why Lexington Air</span>
            <h2 className="section__title">Your Competitive Advantage</h2>
            <p className="section__subtitle">
              We go beyond distribution to provide the tools, training, and support that help you win more jobs.
            </p>
          </div>
          <div className="benefits__grid" style={{ marginTop: 'var(--space-2xl)' }}>
            {[
              { title: 'Instant Spec Sheets', text: 'Complete technical documentation, engineering drawings, and installation guides available on demand.' },
              { title: 'Local Warehouse Stock', text: 'Inventory stocked locally for fast fulfillment — no waiting weeks for shipments from across the country.' },
              { title: 'Wholesale Trade Pricing', text: 'Competitive wholesale rates and volume discounts designed for contractors and builders.' },
              { title: 'Training & Certification', text: 'Hands-on installation training and certification programs to keep your team at the forefront.' },
              { title: 'Expert Tech Support', text: 'Certified technicians available for system selection, sizing, and troubleshooting assistance.' },
              { title: 'New England Coverage', text: 'Serving Massachusetts, New Hampshire, and the broader New England region with dedicated support.' },
            ].map((item) => (
              <div key={item.title} className="benefit">
                <h3 className="benefit__title">{item.title}</h3>
                <p className="benefit__text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners & Certifications */}
      <section className="section section--charcoal" ref={addFadeRef}>
        <div className="container fade-in text-center">
          <span className="section__label">Partners & Certifications</span>
          <h2 className="section__title">Trusted Industry Partners</h2>
          <div className="partners" style={{ marginTop: 'var(--space-2xl)' }}>
            <img src={process.env.PUBLIC_URL + "/imgs/partners/1-tcl-logo.png"} alt="TCL" className="partners__img" />
            <img src={process.env.PUBLIC_URL + "/imgs/partners/6-Energy_Star_logo.png"} alt="Energy Star" className="partners__img" />
            <img src={process.env.PUBLIC_URL + "/imgs/partners/7-AHRI-1080.webp"} alt="AHRI" className="partners__img" />
            <img src={process.env.PUBLIC_URL + "/imgs/partners/4-MassSave.png"} alt="Mass Save" className="partners__img" />
            <img src={process.env.PUBLIC_URL + "/imgs/partners/5-NEHP_logo.png"} alt="NEHPA" className="partners__img" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section section--dark" ref={addFadeRef}>
        <div className="container fade-in text-center">
          <h2 className="section__title">Ready to Get Started?</h2>
          <p className="section__subtitle">
            Contact us for product information, pricing, or to set up a trade account.
          </p>
          <div className="btn-group">
            <Link to="/contact" className="btn btn--primary btn--lg">Contact Us</Link>
            <Link to="/products" className="btn btn--outline-white btn--lg">Browse Products</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
